import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  useComposerRuntime,
  useAuiState,
  ComposerPrimitive,
  MessagePartPrimitive,
  MessagePrimitive,
  useMessagePartText,
} from '@assistant-ui/react';
import { Streamdown, CodeBlock } from 'streamdown';
import {
  PENDING_RESPONSE_WORDS,
  nextPendingFrameDelay,
  pendingWordFromText,
  randomPendingWord,
} from './PendingWords.js';

export const HubAgentContext = createContext(null);

function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

function useAssistantRunActive() {
  const threadIsRunning = useAuiState((state) => Boolean(state.thread.isRunning));
  const [adapterIsRunning, setAdapterIsRunning] = useState(false);

  useEffect(() => {
    const onRunState = (event) => {
      setAdapterIsRunning(Boolean(event?.detail?.running));
    };
    window.addEventListener('hub:assistant-run-state', onRunState);
    return () => window.removeEventListener('hub:assistant-run-state', onRunState);
  }, []);

  return threadIsRunning || adapterIsRunning;
}

export function preloadAgentAvatar(agent, signal) {
  const url = String(agent?.avatar_url || '').trim();
  if (!url || typeof window === 'undefined') return Promise.resolve(true);
  if (signal?.aborted) return Promise.resolve(false);
  return new Promise((resolve) => {
    const img = new Image();
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      signal?.removeEventListener?.('abort', onAbort);
      resolve(ok);
    };
    const onAbort = () => finish(false);
    signal?.addEventListener?.('abort', onAbort, { once: true });
    img.onload = async () => {
      try {
        if (typeof img.decode === 'function') await img.decode();
      } catch {}
      finish(true);
    };
    img.onerror = () => finish(false);
    img.referrerPolicy = 'no-referrer';
    img.src = url;
  });
}

export function useHubAgentState({ preloadAvatar = false } = {}) {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarReady, setAvatarReady] = useState(!preloadAvatar);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setAvatarReady(!preloadAvatar);
    fetch('/api/me/agent', { credentials: 'same-origin', signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then(async (data) => {
        if (cancelled) return;
        if (!data) {
          setAgent(null);
          setAvatarReady(true);
          return;
        }
        if (preloadAvatar) {
          await preloadAgentAvatar(data, controller.signal);
        }
        if (cancelled) return;
        setAgent(data);
        setAvatarReady(true);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setAvatarReady(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      })
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [preloadAvatar]);
  return { agent, loading, avatarReady, error };
}

export function useHubAgentFetch() {
  const { agent } = useHubAgentState();
  return agent;
}

export function OpenClawBeamIndicator() {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const onOpenClawState = (event) => {
      setActive(Boolean(event?.detail?.active));
    };
    const onRunState = (event) => {
      if (!event?.detail?.running) setActive(false);
    };
    window.addEventListener('hub:assistant-openclaw-state', onOpenClawState);
    window.addEventListener('hub:assistant-run-state', onRunState);
    return () => {
      window.removeEventListener('hub:assistant-openclaw-state', onOpenClawState);
      window.removeEventListener('hub:assistant-run-state', onRunState);
    };
  }, []);

  return (
    <div
      className={cx('hub-openclaw-beam', active && 'is-active')}
      aria-hidden="true"
      data-openclaw-active={active ? '1' : '0'}
    >
      <div className="hub-openclaw-beam-rays">
        <span />
        <span />
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="hub-openclaw-beam-core" />
    </div>
  );
}

/* ─── Icons ─── */
export function SparkleIcon({ size = 22 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275Z" />
      <path d="M5 3v4" /><path d="M19 17v4" />
      <path d="M3 5h4" /><path d="M17 19h4" />
    </svg>
  );
}
export function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
export function MonitorIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8" />
      <path d="M12 16v4" />
    </svg>
  );
}
export function ExpandIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
      <path d="M16 3h3a2 2 0 0 1 2 2v3" />
      <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}
export function ShrinkIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 3v3a2 2 0 0 1-2 2H3" />
      <path d="M16 3v3a2 2 0 0 0 2 2h3" />
      <path d="M8 21v-3a2 2 0 0 0-2-2H3" />
      <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
    </svg>
  );
}
export function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
export function PasteIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 4h1a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M9 14h6" />
      <path d="m12 11 3 3-3 3" />
    </svg>
  );
}
export function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
export function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
export function StopIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="7" y="7" width="10" height="10" rx="1.5" />
    </svg>
  );
}
export function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="3" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" /><path d="M9 21h6" />
    </svg>
  );
}

/* ─── Avatar ─── */
export function AgentAvatar({ agent, className = '' }) {
  const [failed, setFailed] = useState(false);
  const cls = `hub-msg-avatar ${className}`.trim();
  if (!agent) return <span className={`${cls} hub-msg-avatar-placeholder`} aria-hidden="true" />;
  if (agent.avatar_url && !failed) {
    return (
      <img className={cls} src={agent.avatar_url} alt=""
           referrerPolicy="no-referrer"
           onError={() => setFailed(true)} />
    );
  }
  return (
    <span className={`${cls} hub-msg-avatar-fallback`} aria-hidden="true">
      {agent.emoji || '✨'}
    </span>
  );
}

export function AgentIdentityMark({ className = '' }) {
  const agent = useContext(HubAgentContext);
  return <AgentAvatar agent={agent} className={className} />;
}

function isInternalHref(href) {
  if (!href || href === 'streamdown:incomplete-link') return true;
  if (href.startsWith('/') || href.startsWith('#')) return true;
  try {
    const url = new URL(href, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
}

function textFromChildren(children) {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(textFromChildren).join('');
  return '';
}

// Hub Files links (uploaded attachments, or files the agent shares back)
// render as a file card with a Download button instead of a bare text link.
function isHubFileHref(href) {
  if (!href || typeof window === 'undefined') return false;
  try {
    const u = new URL(href, window.location.origin);
    return u.origin === window.location.origin && u.pathname.startsWith('/hub-files/');
  } catch {
    return false;
  }
}

const FILE_EXT_RE = /\.([a-z0-9]{1,8})$/i;

function FileCardLink({ href, label }) {
  let name = String(label || '').trim();
  if (!name || /^https?:\/\//i.test(name)) {
    try { name = decodeURIComponent(String(href).split('/').pop().split('?')[0]); } catch { name = 'file'; }
  }
  const ext = (FILE_EXT_RE.exec(name) || [])[1]?.toUpperCase() || '';
  return (
    <span className="hub-gw2-filecard">
      <a className="hub-gw2-filecard-main" href={href} target="_blank" rel="noopener noreferrer" title={name}>
        <span className="hub-gw2-filecard-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        </span>
        <span className="hub-gw2-filecard-text">
          <span className="hub-gw2-filecard-name">{name}</span>
          <span className="hub-gw2-filecard-meta">{ext ? `${ext} · ` : ''}Hub Files</span>
        </span>
      </a>
      <a className="hub-gw2-filecard-dl" href={href} download title={`Download ${name}`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <span>Download</span>
      </a>
    </span>
  );
}

function MarkdownLink({ children, className = '', href = '', node, ...props }) {
  const internal = isInternalHref(href);
  const safeHref = href === 'streamdown:incomplete-link' ? undefined : href;
  const childText = textFromChildren(children).trim();
  if (safeHref && isHubFileHref(safeHref)) {
    return <FileCardLink href={safeHref} label={childText} />;
  }
  const normalizedChild = childText.replace(/\/$/, '');
  const normalizedHref = String(safeHref || '').replace(/\/$/, '');
  const isBareUrl = Boolean(
    safeHref && childText && (
      normalizedChild === normalizedHref ||
      /^https?:\/\//i.test(childText)
    ),
  );
  return (
    <a
      className={cx(
        'hub-sd-link',
        internal ? 'is-internal' : 'is-external',
        isBareUrl ? 'is-bare-url' : '',
        className,
      )}
      data-streamdown="link"
      {...props}
      href={safeHref}
      rel={internal ? undefined : 'noopener noreferrer'}
      target={internal ? undefined : '_blank'}
    >
      {children}
    </a>
  );
}

function MarkdownParagraph({ children, className = '', node, ...props }) {
  return <p className={cx('hub-sd-p', className)} {...props}>{children}</p>;
}

function MarkdownList({ as: Tag, className = '', children, node, ...props }) {
  const streamdownType = Tag === 'ol' ? 'ordered-list' : 'unordered-list';
  return (
    <Tag className={cx('hub-sd-list', className)} {...props} data-streamdown={streamdownType}>
      {children}
    </Tag>
  );
}

function MarkdownListItem({ children, className = '', node, ...props }) {
  return <li className={cx('hub-sd-list-item', className)} {...props} data-streamdown="list-item">{children}</li>;
}

function MarkdownHeading({ as: Tag, children, className = '', node, ...props }) {
  return (
    <Tag className={cx('hub-sd-heading', className)} {...props} data-streamdown={`heading-${Tag.slice(1)}`}>
      {children}
    </Tag>
  );
}

function MarkdownInlineCode({ children, className = '', node, ...props }) {
  return <code className={cx('hub-sd-inline-code', className)} {...props} data-streamdown="inline-code">{children}</code>;
}

function MarkdownBlockquote({ children, className = '', node, ...props }) {
  return <blockquote className={cx('hub-sd-blockquote', className)} {...props} data-streamdown="blockquote">{children}</blockquote>;
}

function MarkdownImage({ className = '', alt = '', node, ...props }) {
  return <img className={cx('hub-sd-image', className)} alt={alt} loading="lazy" {...props} data-streamdown="image" />;
}

// ── Fish-style terminal for shell code fences ─────────────────────────────
// Same lobster terminal + shell highlighter the exec tool cards use, so a
// bash command the assistant writes reads like a real terminal instead of a
// generic code block. (Self-contained here to avoid a shared/gatewayParts
// import cycle; token classes/CSS are the shared .hub-gw2-shell / .fsh-* set.)
const SD_SHELL_TOKEN_RE = /(\s+)|("(?:\\.|[^"\\])*"|'[^']*')|(\$\{[^}]*\}|\$\w+)|(&&|\|\||\$\(|[|;&()<>]+)|(-{1,2}[A-Za-z0-9][\w-]*)|([^\s|;&()<>'"]+)/g;
function sdLooksLikePath(w) { return /[/~]/.test(w) || /^[A-Za-z0-9._-]+:[^\s]/.test(w); }
function sdHighlightShell(src) {
  const out = [];
  let last = 0, i = 0, expectCommand = true, m;
  SD_SHELL_TOKEN_RE.lastIndex = 0;
  while ((m = SD_SHELL_TOKEN_RE.exec(src)) !== null) {
    if (m.index > last) out.push(src.slice(last, m.index));
    if (m[1] !== undefined) out.push(m[1]);
    else if (m[2] !== undefined) out.push(<span key={i++} className="fsh-str">{m[2]}</span>);
    else if (m[3] !== undefined) out.push(<span key={i++} className="fsh-var">{m[3]}</span>);
    else if (m[4] !== undefined) { out.push(<span key={i++} className="fsh-op">{m[4]}</span>); if (/[|;&(]/.test(m[4])) expectCommand = true; }
    else if (m[5] !== undefined) out.push(<span key={i++} className="fsh-flag">{m[5]}</span>);
    else if (m[6] !== undefined) {
      if (expectCommand) { out.push(<span key={i++} className="fsh-cmd">{m[6]}</span>); expectCommand = false; }
      else if (sdLooksLikePath(m[6])) out.push(<span key={i++} className="fsh-path">{m[6]}</span>);
      else out.push(<span key={i++} className="fsh-arg">{m[6]}</span>);
    }
    last = SD_SHELL_TOKEN_RE.lastIndex;
  }
  if (last < src.length) out.push(src.slice(last));
  return out;
}
function FishTerminal({ command }) {
  const clean = String(command ?? '').replace(/\s+$/, '');
  const multiline = clean.indexOf('\n') !== -1;
  return (
    <div className="hub-gw2-shell hub-gw2-shell-md">
      <pre className="hub-gw2-shell-body">
        <span className="hub-gw2-shell-prompt"><span className="fsh-lobster">🦞</span> <span className="fsh-pwd">~/hub</span> <span className="fsh-arrow">❯</span> </span>
        <code>{sdHighlightShell(multiline ? clean : clean)}</code>
      </pre>
    </div>
  );
}
const SD_SHELL_LANGS = new Set(['bash', 'sh', 'shell', 'zsh', 'console', 'shell-session', 'shellsession', 'shellscript', 'terminal', 'command']);
function codeChildrenToString(children) {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(codeChildrenToString).join('');
  if (children && typeof children === 'object' && children.props) return codeChildrenToString(children.props.children);
  return children == null ? '' : String(children);
}
// Block code fences only (streamdown routes inline code to `inlineCode`).
function MarkdownCode({ className = '', children, ...props }) {
  const m = /language-([\w.+-]+)/.exec(className || '');
  const lang = m ? m[1].toLowerCase() : '';
  if (SD_SHELL_LANGS.has(lang)) {
    return <FishTerminal command={codeChildrenToString(children)} />;
  }
  // Non-shell: hand back to streamdown's own CodeBlock (code/language API) so it
  // keeps its default header/copy/highlighting. Rendered inside <Streamdown>, so
  // its context is available.
  return <CodeBlock code={codeChildrenToString(children)} language={lang || 'text'} />;
}

export const streamdownComponents = {
  a: MarkdownLink,
  code: MarkdownCode,
  p: MarkdownParagraph,
  ul: (props) => <MarkdownList as="ul" {...props} />,
  ol: (props) => <MarkdownList as="ol" {...props} />,
  li: MarkdownListItem,
  h1: (props) => <MarkdownHeading as="h1" {...props} />,
  h2: (props) => <MarkdownHeading as="h2" {...props} />,
  h3: (props) => <MarkdownHeading as="h3" {...props} />,
  h4: (props) => <MarkdownHeading as="h4" {...props} />,
  h5: (props) => <MarkdownHeading as="h5" {...props} />,
  h6: (props) => <MarkdownHeading as="h6" {...props} />,
  inlineCode: MarkdownInlineCode,
  blockquote: MarkdownBlockquote,
  img: MarkdownImage,
};

// Reusable markdown body — same Streamdown renderer the assistant FAB uses
// for assistant deltas. Pass `streaming` while text is mid-stream so the
// renderer applies the word-by-word fade-in.
export function HubMarkdownBody({ text, streaming = false, className = '' }) {
  return (
    <div className={cx('hub-msg-markdown', className)} data-status={streaming ? 'running' : 'complete'}>
      <Streamdown
        className="hub-streamdown"
        mode={streaming ? 'streaming' : 'static'}
        animated={streaming ? { animation: 'fadeIn', duration: 120, sep: 'word', stagger: 8 } : false}
        components={streamdownComponents}
        isAnimating={streaming}
        linkSafety={{ enabled: false }}
      >
        {text || ''}
      </Streamdown>
    </div>
  );
}

function PendingAssistantStatus({ word }) {
  const { elapsedLabel, tokenLabel } = usePendingActivityMetrics();
  const baseWord = word || 'Thinking';
  return (
    <div className="hub-pending-status" aria-live="polite">
      <span className="hub-pending-copy">
        <span className="hub-pending-word" key={word}>{baseWord}</span>
        <span className="hub-pending-dots" aria-hidden="true">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
      </span>
      <span className="hub-pending-meta" aria-hidden="true">
        <span className="hub-pending-metric">
          <PendingClockIcon />
          <span className="hub-pending-number hub-pending-elapsed-value">{elapsedLabel}</span>
        </span>
        <span className="hub-pending-meta-dot">&bull;</span>
        <span className="hub-pending-metric">
          <PendingTokenIcon />
          <span className="hub-pending-number hub-pending-token-value">{tokenLabel}</span>
        </span>
      </span>
      <span className="sr-only">{baseWord}… {elapsedLabel}. {tokenLabel}.</span>
    </div>
  );
}

function PendingClockIcon() {
  return (
    <svg className="hub-pending-meta-icon" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.7v3.6l2.4 1.4" />
    </svg>
  );
}

function PendingTokenIcon() {
  return (
    <svg className="hub-pending-meta-icon hub-pending-token-icon" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="5.8" />
      <ellipse cx="8" cy="8" rx="2.9" ry="5.8" />
      <path d="M3.2 8h9.6" />
    </svg>
  );
}

function formatPendingElapsed(totalSeconds) {
  const seconds = Math.max(0, Math.floor(totalSeconds || 0));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}m ${rest}s`;
}

function formatPendingTokens(value) {
  const tokens = Math.max(1, Math.floor(value || 1));
  if (tokens < 1000) return `${tokens} tokens`;
  return `${(tokens / 1000).toFixed(1)}k tokens`;
}

function usePendingActivityMetrics() {
  const startRef = useRef(Date.now());
  const shapeRef = useRef(null);
  const tokenMotionRef = useRef(null);
  const [metrics, setMetrics] = useState(() => ({
    elapsedMs: 0,
    tokenCount: 1,
  }));

  if (!shapeRef.current) {
    const seed = Math.random();
    shapeRef.current = {
      baseTokens: 48 + Math.round(seed * 120),
      tokensPerSecond: 13 + seed * 13,
      curve: 12 + Math.random() * 18,
      burstiness: 0.55 + Math.random() * 0.45,
    };
  }

  useEffect(() => {
    const shape = shapeRef.current;
    const now = Date.now();
    tokenMotionRef.current = {
      displayedTokens: shape.baseTokens,
      currentRate: shape.tokensPerSecond,
      targetRate: shape.tokensPerSecond,
      lastTickAt: now,
      nextRateShiftAt: now + 280 + Math.random() * 720,
    };

    const update = () => {
      const tickAt = Date.now();
      const elapsedMs = Math.max(0, tickAt - startRef.current);
      const motion = tokenMotionRef.current;
      const deltaSeconds = Math.min(0.5, Math.max(0, (tickAt - motion.lastTickAt) / 1000));
      motion.lastTickAt = tickAt;

      if (tickAt >= motion.nextRateShiftAt) {
        const swing = 1 + (Math.random() - 0.5) * shape.burstiness * 2.2;
        motion.targetRate = Math.max(6, shape.tokensPerSecond * swing);
        motion.nextRateShiftAt = tickAt + 260 + Math.random() * 920;
      }

      const easing = 1 - Math.pow(0.16, deltaSeconds * 5);
      motion.currentRate += (motion.targetRate - motion.currentRate) * easing;
      motion.displayedTokens += (motion.currentRate + Math.log1p(elapsedMs / 1000) * shape.curve) * deltaSeconds;

      setMetrics({
        elapsedMs,
        tokenCount: motion.displayedTokens,
      });
    };
    update();
    const interval = window.setInterval(update, 120);
    return () => window.clearInterval(interval);
  }, []);

  const elapsedSeconds = metrics.elapsedMs / 1000;

  return {
    elapsedLabel: formatPendingElapsed(Math.floor(elapsedSeconds)),
    tokenLabel: formatPendingTokens(metrics.tokenCount),
  };
}

function useRotatingPendingWord() {
  const [word, setWord] = useState(() => randomPendingWord());
  useEffect(() => {
    let timeout = 0;
    const rotate = () => {
      setWord((value) => randomPendingWord(value));
      timeout = window.setTimeout(rotate, nextPendingFrameDelay());
    };
    timeout = window.setTimeout(rotate, nextPendingFrameDelay());
    return () => window.clearTimeout(timeout);
  }, []);
  return word || PENDING_RESPONSE_WORDS[0] || 'Thinking';
}

function EmptyMessagePart({ status }) {
  const word = useRotatingPendingWord();
  if (status?.type !== 'running') return null;
  return (
    <div className="hub-msg-markdown hub-msg-pending" data-status="running">
      <PendingAssistantStatus word={word} />
    </div>
  );
}

function MarkdownTextPart() {
  const { text, status } = useMessagePartText();
  const isRunning = status?.type === 'running';
  const pendingWord = pendingWordFromText(text);
  if (pendingWord) {
    return (
      <div className="hub-msg-markdown hub-msg-pending" data-status={status?.type || 'running'}>
        <PendingAssistantStatus word={pendingWord} />
      </div>
    );
  }
  return (
    <div className="hub-msg-markdown" data-status={status?.type || 'complete'}>
      <Streamdown
        className="hub-streamdown"
        mode={isRunning ? 'streaming' : 'static'}
        animated={isRunning ? { animation: 'fadeIn', duration: 120, sep: 'word', stagger: 8 } : false}
        components={streamdownComponents}
        isAnimating={isRunning}
        linkSafety={{ enabled: false }}
      >
        {text || ''}
      </Streamdown>
      <MessagePartPrimitive.InProgress>
        <span className="hub-stream-dot" aria-hidden="true"> ●</span>
      </MessagePartPrimitive.InProgress>
    </div>
  );
}

const messagePartComponents = {
  Text: MarkdownTextPart,
  Empty: EmptyMessagePart,
};

function MessageParts() {
  return <MessagePrimitive.Parts components={messagePartComponents} />;
}

/* ─── Messages ─── */
export function UserMessage() {
  return (
    <MessagePrimitive.Root className="hub-msg hub-msg-user">
      <div className="hub-msg-bubble">
        <MessageParts />
      </div>
    </MessagePrimitive.Root>
  );
}

export function AssistantMessage() {
  return (
    <MessagePrimitive.Root className="hub-msg hub-msg-assistant">
      <HubAgentContext.Consumer>
        {(agent) => <AgentAvatar agent={agent} />}
      </HubAgentContext.Consumer>
      <div className="hub-msg-bubble">
        <MessageParts />
      </div>
    </MessagePrimitive.Root>
  );
}

/* ─── Mic + Composer ─── */
export function MicButton() {
  const composer = useComposerRuntime();
  const [recording, setRecording] = useState(false);
  const [supported, setSupported] = useState(false);
  const recRef = useRef(null);
  const baseRef = useRef('');

  useEffect(() => {
    const SR = typeof window !== 'undefined'
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;
    setSupported(Boolean(SR));
  }, []);

  if (!supported) return null;

  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || 'en-US';
    rec.onstart = () => {
      baseRef.current = composer.getState?.()?.text || '';
      setRecording(true);
    };
    rec.onerror = () => setRecording(false);
    rec.onend = () => setRecording(false);
    rec.onresult = (e) => {
      let transcript = '';
      for (let i = 0; i < e.results.length; i++) transcript += e.results[i][0].transcript;
      const base = baseRef.current;
      const joined = base ? `${base.replace(/\s+$/, '')} ${transcript}` : transcript;
      composer.setText(joined);
    };
    recRef.current = rec;
    try { rec.start(); } catch { setRecording(false); }
  };
  const stop = () => {
    try { recRef.current?.stop(); } catch {}
    setRecording(false);
  };

  return (
    <button type="button"
            className={`hub-composer-mic ${recording ? 'is-recording' : ''}`}
            onClick={() => (recording ? stop() : start())}
            aria-label={recording ? 'Stop transcription' : 'Start voice transcription'}
            title={recording ? 'Stop' : 'Voice input'}>
      <MicIcon />
      {recording && <span className="hub-mic-pulse" aria-hidden="true" />}
    </button>
  );
}

export function Composer({ placeholder = 'Message the assistant…' }) {
  const isRunning = useAssistantRunActive();

  return (
    <ComposerPrimitive.Root className="hub-composer">
      <ComposerPrimitive.Input
        className="hub-composer-input"
        placeholder={placeholder}
        autoFocus
        rows={1}
      />
      <div className="hub-composer-actions">
        <MicButton />
        {isRunning && (
          <ComposerPrimitive.Cancel
            className="hub-composer-cancel"
            aria-label="Stop response"
            title="Stop response"
          >
            <StopIcon />
          </ComposerPrimitive.Cancel>
        )}
        <ComposerPrimitive.Send className="hub-composer-send" aria-label="Send">
          <SendIcon />
        </ComposerPrimitive.Send>
      </div>
    </ComposerPrimitive.Root>
  );
}
